import { useSearchParams } from 'react-router-dom';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import {
  createVector,
  type CreateVectorInput,
  deleteVector,
  type DeleteVectorInput,
  updateVector,
  type UpdateVectorInput,
  updateVectorOrder,
  type UpdateVectorOrderInput,
} from '../../api/vectors';
import { type DefaultMutationError } from '../index';

export const useCreateVector = (
  opts?: Partial<
    UseMutationOptions<Awaited<any>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['createScenarioVector'],
    mutationFn: async (input: CreateVectorInput) => {
      const { data }: AxiosResponse<any> = await createVector(input);

      return data;
    },
    async onSuccess(data, variables, unknown, context) {
      searchParams.delete('isActualData');
      setSearchParams(searchParams);

      await queryClient.refetchQueries({
        queryKey: ['getScenarioVectors'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getScenarioData'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getBaseScenarioData'],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutput',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      (await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutputWithActualData',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      }),
        opts?.onSuccess?.(data, variables, unknown, context));
    },
  });
};

export const useUpdateVector = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['updateVector'],
    mutationFn: async (input: UpdateVectorInput) => {
      const { data }: AxiosResponse = await updateVector(input);
      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      searchParams.delete('isActualData');
      setSearchParams(searchParams);

      await queryClient.refetchQueries({
        queryKey: ['getScenarioVectors'],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutput',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutputWithActualData',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.refetchQueries({
        queryKey: [
          'getScenarioListDisplay',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
        exact: true,
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useDeleteVector = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['deleteVector'],
    mutationFn: async (input: DeleteVectorInput) => {
      const { data }: AxiosResponse = await deleteVector(input);

      return data;
    },
    async onSuccess(data, variables, unknown, context) {
      searchParams.delete('isActualData');
      setSearchParams(searchParams);

      await queryClient.refetchQueries({
        queryKey: ['getScenarioVectors'],
      });
      await queryClient.refetchQueries({
        queryKey: ['getScenarioData'],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutput',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'getModelOutputWithActualData',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.refetchQueries({
        queryKey: ['getBaseScenarioData'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useUpdateVectorOrder = (
  opts?: Partial<
    UseMutationOptions<Awaited<AxiosResponse>, DefaultMutationError, any, any>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateVectorOrder'],
    mutationFn: async (input: UpdateVectorOrderInput) => {
      const { data }: AxiosResponse = await updateVectorOrder(input);

      return data;
    },
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getScenarioVectors'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};
